import React, { useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';

import {
  associateSoftwareToken,
  verifySoftwareToken,
} from '../../../services/auth';
import { MFAMethod } from '../../../services/auth/types';
import {
  useUpdateUserMfaMethodMutation,
} from '../../../services/users';
import Button from '../../Button';
import CollapsableNotification from '../../CollapsableNotification';
import CopyButton from '../../CopyButton';
import FixedLengthField from '../../FixedLengthField';
import {
  ButtonContainer,
  FormContainer,
  GuideBody,
  GuideTextContainer,
  GuideTitle,
  QRCodeContainer,
} from '../index.styles';

interface TOTPSetupFormProps {
  user: CognitoUser | null;
  email: string;
  onComplete: () => void;
}

interface FormValues {
  verification: string;
}

const TOTPSetupForm = ({ user, email, onComplete }: TOTPSetupFormProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();

  const [secretCode, setSecretCode] = useState<undefined | string>(undefined);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [updateUserMfaMethod] = useUpdateUserMfaMethodMutation();

  useEffect(() => {
    const setupMFA = async () => {
      if (user) {
        try {
          const code = await associateSoftwareToken(user);
          setSecretCode(code);
        } catch (error: any) {
          console.error(error);
          setErrorMessage(error?.message || 'An error has occurred');
        }
      }
    };

    setupMFA();
  }, [user]);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);

    try {
      if (user) {
        await verifySoftwareToken(user, data.verification);

        // Indicate that the user's preferred MFA method is TOTP
        // and that it has been set up
        await updateUserMfaMethod({
          userId: user.getUsername(),
          mfaMethod: MFAMethod.TOTP,
          isTotpSetup: true,
        });

        onComplete();
      }
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      setErrorMessage(error?.message || 'An error has occurred');
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <GuideTextContainer>
        <GuideTitle variant="h5">
          Set up your authenticator
        </GuideTitle>
        <GuideBody variant="caption">
          Scan the QR code with your authenticator app,
          then enter the code generated by the app.
        </GuideBody>
      </GuideTextContainer>
      <CollapsableNotification
        open={!!errorMessage}
        close={() => setErrorMessage('')}
        message={errorMessage}
      />
      {secretCode ? (
        <QRCodeContainer>
          <QRCode
            value={`otpauth://totp/Cognito%20Online:${email}?secret=${secretCode}&issuer=Cognito%20Online`}
            size={200}
          />
          <CopyButton
            copyText={secretCode}
            buttonLabel="Copy Setup Key Instead"
            size="small"
            variant="outlined"
          />
        </QRCodeContainer>
      ) : <Skeleton variant="rectangular" width={200} height={200} />}
      <FixedLengthField
        error={!!errors.verification}
        errorMessage={errors.verification ? 'An error has occurred' : ''}
        maxLength={6}
        numbersOnly
        onFilled={(code) => onSubmit({ verification: code })}
        {...register('verification', { required: true, pattern: /\d{6}/ })}
      />
      <ButtonContainer>
        <Button variant="contained" type="submit" loading={loading} disabled={!secretCode}>
          Verify
        </Button>
        <Button variant="outlined" onClick={onComplete}>
          Not now
        </Button>
      </ButtonContainer>
    </FormContainer>
  );
};

export default TOTPSetupForm;
